import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Edit2, Trash2, Save, X, Calendar, Play } from "lucide-react";
import {
    fetchAutoTransactions,
    createAutoTransaction,
    updateAutoTransaction,
    deleteAutoTransaction,
    triggerAutoTransactionsNow,
    fetchIncomeCategories,
    fetchExpenseCategories,
} from "../services/api";

// Mobile Card Component - Manages its own edit state
const AutoTransactionCard = React.memo(({
    tx,
    index,
    incomeCategories,
    expenseCategories,
    onSave,
    onDelete,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editType, setEditType] = useState(tx.transaction_type);
    const [editCategory, setEditCategory] = useState(tx.transaction_category);
    const [editAmount, setEditAmount] = useState(tx.transaction_amount);
    const [editDay, setEditDay] = useState(tx.day_of_month);
    const [editDescription, setEditDescription] = useState(tx.description || "");
    const [saveError, setSaveError] = useState(null);

    const getCurrentCategories = (type) => {
        return type === "income" ? incomeCategories : expenseCategories;
    };

    const handleEdit = () => {
        setIsEditing(true);
        setEditType(tx.transaction_type);
        setEditCategory(tx.transaction_category);
        setEditAmount(tx.transaction_amount);
        setEditDay(tx.day_of_month);
        setEditDescription(tx.description || "");
        setSaveError(null);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditType(tx.transaction_type);
        setEditCategory(tx.transaction_category);
        setEditAmount(tx.transaction_amount);
        setEditDay(tx.day_of_month);
        setEditDescription(tx.description || "");
        setSaveError(null);
    };

    const handleSave = async () => {
        try {
            await onSave(tx.id_auto_transaction, {
                transaction_type: editType,
                transaction_category: editCategory,
                transaction_amount: Number(editAmount),
                day_of_month: Number(editDay),
                description: editDescription,
            });
            setIsEditing(false);
            setSaveError(null);
        } catch (err) {
            setSaveError(err.message || "Failed to update");
        }
    };

    const handleDelete = () => {
        onDelete(tx.id_auto_transaction);
    };

    const handleTypeChange = (newType) => {
        setEditType(newType);
        setEditCategory(""); // Reset category when type changes
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, delay: index * 0.04 }}
            className="bg-gray-750 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors"
        >
            {isEditing ? (
                <div className="space-y-3">
                    {/* Type */}
                    <div>
                        <label className="text-xs text-gray-400 mb-1 block">Type</label>
                        <select
                            value={editType}
                            onChange={(e) => handleTypeChange(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 
                                       focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                        >
                            <option value="income">Income</option>
                            <option value="expense">Expense</option>
                        </select>
                    </div>

                    {/* Category */}
                    <div>
                        <label className="text-xs text-gray-400 mb-1 block">Category</label>
                        <select
                            value={editCategory}
                            onChange={(e) => setEditCategory(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 
                                       focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                        >
                            {getCurrentCategories(editType).map((cat) => (
                                <option
                                    key={editType === "income" ? cat.id_income_category : cat.id_expense_category}
                                    value={editType === "income" ? cat.income_category_name : cat.expense_category_name}
                                >
                                    {editType === "income" ? cat.income_category_name : cat.expense_category_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Amount */}
                    <div>
                        <label className="text-xs text-gray-400 mb-1 block">Amount</label>
                        <input
                            type="number"
                            step="0.01"
                            value={editAmount}
                            onChange={(e) => setEditAmount(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 
                                       focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                            autoFocus
                        />
                    </div>

                    {/* Day of Month */}
                    <div>
                        <label className="text-xs text-gray-400 mb-1 block">Day of Month</label>
                        <input
                            type="number"
                            min="1"
                            max="31"
                            value={editDay}
                            onChange={(e) => setEditDay(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 
                                       focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="text-xs text-gray-400 mb-1 block">Description</label>
                        <input
                            type="text"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 
                                       focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                        />
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-2 pt-2">
                        <button
                            onClick={handleSave}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-green-400 
                                       bg-green-400/10 hover:bg-green-400/20 rounded-md transition-colors text-sm"
                        >
                            <Save size={16} />
                            Save
                        </button>
                        <button
                            onClick={handleCancel}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-gray-400 
                                       bg-gray-600/50 hover:bg-gray-600 rounded-md transition-colors text-sm"
                        >
                            <X size={16} />
                            Cancel
                        </button>
                    </div>
                    {saveError && (
                        <p className="text-red-400 text-xs">{saveError}</p>
                    )}
                </div>
            ) : (
                <div>
                    <div className="flex justify-between items-start mb-3">
                        <div>
                            <p className="text-xs uppercase tracking-wide text-gray-400">{`Day ${tx.day_of_month}`}</p>
                            <p
                                className={`text-base font-semibold ${tx.transaction_type === "income" ? "text-green-400" : "text-red-400"
                                    }`}
                            >
                                {tx.transaction_type === "income" ? "+" : "-"}$
                                {Number(tx.transaction_amount).toFixed(2)}
                            </p>
                            <p className="text-gray-200 font-medium">{tx.transaction_category}</p>
                            {tx.description && <p className="text-gray-400 text-sm">{tx.description}</p>}
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={handleEdit}
                                className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-md transition-colors"
                                title="Edit"
                            >
                                <Edit2 size={18} />
                            </button>
                            <button
                                onClick={handleDelete}
                                className="p-2 text-red-400 hover:bg-red-400/10 rounded-md transition-colors"
                                title="Delete"
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </motion.div>
    );
}, (prevProps, nextProps) => {
    // Custom comparison - only re-render if transaction data changed
    return (
        prevProps.tx.id_auto_transaction === nextProps.tx.id_auto_transaction &&
        prevProps.tx.transaction_type === nextProps.tx.transaction_type &&
        prevProps.tx.transaction_category === nextProps.tx.transaction_category &&
        prevProps.tx.transaction_amount === nextProps.tx.transaction_amount &&
        prevProps.tx.day_of_month === nextProps.tx.day_of_month &&
        prevProps.tx.description === nextProps.tx.description &&
        prevProps.incomeCategories.length === nextProps.incomeCategories.length &&
        prevProps.expenseCategories.length === nextProps.expenseCategories.length
    );
});

AutoTransactionCard.displayName = 'AutoTransactionCard';

// Desktop Table Row Component - Manages its own edit state
const AutoTransactionTableRow = React.memo(({
    tx,
    index,
    incomeCategories,
    expenseCategories,
    onSave,
    onDelete,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editType, setEditType] = useState(tx.transaction_type);
    const [editCategory, setEditCategory] = useState(tx.transaction_category);
    const [editAmount, setEditAmount] = useState(tx.transaction_amount);
    const [editDay, setEditDay] = useState(tx.day_of_month);
    const [editDescription, setEditDescription] = useState(tx.description || "");
    const [saveError, setSaveError] = useState(null);

    const getCurrentCategories = (type) => {
        return type === "income" ? incomeCategories : expenseCategories;
    };

    const handleEdit = () => {
        setIsEditing(true);
        setEditType(tx.transaction_type);
        setEditCategory(tx.transaction_category);
        setEditAmount(tx.transaction_amount);
        setEditDay(tx.day_of_month);
        setEditDescription(tx.description || "");
        setSaveError(null);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditType(tx.transaction_type);
        setEditCategory(tx.transaction_category);
        setEditAmount(tx.transaction_amount);
        setEditDay(tx.day_of_month);
        setEditDescription(tx.description || "");
        setSaveError(null);
    };

    const handleSave = async () => {
        try {
            await onSave(tx.id_auto_transaction, {
                transaction_type: editType,
                transaction_category: editCategory,
                transaction_amount: Number(editAmount),
                day_of_month: Number(editDay),
                description: editDescription,
            });
            setIsEditing(false);
            setSaveError(null);
        } catch (err) {
            setSaveError(err.message || "Failed to update");
        }
    };

    const handleDelete = () => {
        onDelete(tx.id_auto_transaction);
    };

    const handleTypeChange = (newType) => {
        setEditType(newType);
        setEditCategory(""); // Reset category when type changes
    };

    return (
        <motion.tr
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="border-t border-gray-700 hover:bg-gray-750 transition-colors"
        >
            <td className="px-6 py-4">
                {isEditing ? (
                    <select
                        value={editType}
                        onChange={(e) => handleTypeChange(e.target.value)}
                        className="px-2 py-1 bg-gray-700 text-white rounded border border-gray-600 text-sm"
                    >
                        <option value="income">Income</option>
                        <option value="expense">Expense</option>
                    </select>
                ) : (
                    <span
                        className={`inline-block px-2 py-1 rounded text-xs font-semibold ${tx.transaction_type === "income"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                            }`}
                    >
                        {tx.transaction_type}
                    </span>
                )}
            </td>

            <td className="px-6 py-4 text-gray-200">
                {isEditing ? (
                    <select
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                        className="w-full px-2 py-1 bg-gray-700 text-white rounded border border-gray-600 text-sm"
                    >
                        {getCurrentCategories(editType).map((cat) => (
                            <option
                                key={editType === "income" ? cat.id_income_category : cat.id_expense_category}
                                value={editType === "income" ? cat.income_category_name : cat.expense_category_name}
                            >
                                {editType === "income" ? cat.income_category_name : cat.expense_category_name}
                            </option>
                        ))}
                    </select>
                ) : (
                    tx.transaction_category
                )}
            </td>

            <td
                className={`px-6 py-4 font-semibold ${tx.transaction_type === "income" ? "text-green-400" : "text-red-400"
                    }`}
            >
                {isEditing ? (
                    <input
                        type="number"
                        step="0.01"
                        value={editAmount}
                        onChange={(e) => setEditAmount(e.target.value)}
                        className="w-24 px-2 py-1 bg-gray-700 text-white rounded border border-gray-600 text-sm"
                    />
                ) : (
                    `$${Number(tx.transaction_amount).toFixed(2)}`
                )}
            </td>

            <td className="px-6 py-4 text-gray-200">
                {isEditing ? (
                    <input
                        type="number"
                        min="1"
                        max="31"
                        value={editDay}
                        onChange={(e) => setEditDay(e.target.value)}
                        className="w-16 px-2 py-1 bg-gray-700 text-white rounded border border-gray-600 text-sm"
                    />
                ) : (
                    `Day ${tx.day_of_month}`
                )}
            </td>

            <td className="px-6 py-4 text-gray-400 text-sm">
                {isEditing ? (
                    <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="w-full px-2 py-1 bg-gray-700 text-white rounded border border-gray-600 text-sm"
                    />
                ) : (
                    tx.description || "—"
                )}
            </td>

            <td className="px-6 py-4">
                <div className="flex items-center justify-end gap-2">
                    {isEditing ? (
                        <>
                            <button
                                onClick={handleSave}
                                className="p-2 text-green-400 hover:bg-green-400/10 rounded-lg transition-colors"
                                title="Save"
                            >
                                <Save size={18} />
                            </button>
                            <button
                                onClick={handleCancel}
                                className="p-2 text-gray-400 hover:bg-gray-600 rounded-lg transition-colors"
                                title="Cancel"
                            >
                                <X size={18} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={handleEdit}
                                className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                                title="Edit"
                            >
                                <Edit2 size={18} />
                            </button>
                            <button
                                onClick={handleDelete}
                                className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                title="Delete"
                            >
                                <Trash2 size={18} />
                            </button>
                        </>
                    )}
                </div>
                {saveError && (
                    <p className="text-red-400 text-xs mt-1 text-right">
                        {saveError}
                    </p>
                )}
            </td>
        </motion.tr>
    );
}, (prevProps, nextProps) => {
    // Custom comparison
    return (
        prevProps.tx.id_auto_transaction === nextProps.tx.id_auto_transaction &&
        prevProps.tx.transaction_type === nextProps.tx.transaction_type &&
        prevProps.tx.transaction_category === nextProps.tx.transaction_category &&
        prevProps.tx.transaction_amount === nextProps.tx.transaction_amount &&
        prevProps.tx.day_of_month === nextProps.tx.day_of_month &&
        prevProps.tx.description === nextProps.tx.description &&
        prevProps.incomeCategories.length === nextProps.incomeCategories.length &&
        prevProps.expenseCategories.length === nextProps.expenseCategories.length
    );
});

AutoTransactionTableRow.displayName = 'AutoTransactionTableRow';

// Main Component
export default function AutoTransactionManager({ onAutoTransactionsChanged }) {
    const [autoTransactions, setAutoTransactions] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [incomeCategories, setIncomeCategories] = useState([]);
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // New transaction form state
    const [newTransaction, setNewTransaction] = useState({
        transaction_type: "expense",
        transaction_category: "",
        transaction_amount: "",
        day_of_month: "1",
        description: "",
    });

    const loadAutoTransactions = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchAutoTransactions();
            setAutoTransactions(data);
            setError(null);
        } catch (err) {
            console.error("Error fetching auto transactions:", err);
            setError("Failed to load automatic transactions");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadCategories = useCallback(async () => {
        try {
            const [income, expense] = await Promise.all([
                fetchIncomeCategories(),
                fetchExpenseCategories(),
            ]);
            setIncomeCategories(income);
            setExpenseCategories(expense);
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    }, []);

    useEffect(() => {
        loadAutoTransactions();
        loadCategories();
    }, [loadAutoTransactions, loadCategories]);

    const getCurrentCategories = (type) => {
        return type === "income" ? incomeCategories : expenseCategories;
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        try {
            await createAutoTransaction({
                ...newTransaction,
                transaction_amount: Number(newTransaction.transaction_amount),
                day_of_month: Number(newTransaction.day_of_month),
            });
            await loadAutoTransactions();
            if (onAutoTransactionsChanged) onAutoTransactionsChanged(); // ✅ trigger refresh
            setShowAddForm(false);
            setNewTransaction({
                transaction_type: "expense",
                transaction_category: "",
                transaction_amount: "",
                day_of_month: "1",
                description: "",
            });
        } catch (err) {
            alert(err.message || "Failed to add automatic transaction");
        }
    };

    const handleSave = useCallback(async (txId, values) => {
        await updateAutoTransaction(txId, values);
        await loadAutoTransactions();
        if (onAutoTransactionsChanged) onAutoTransactionsChanged(); // ✅ trigger refresh
    }, [loadAutoTransactions, onAutoTransactionsChanged]);

    const handleDelete = useCallback(async (txId) => {
        if (!window.confirm("Are you sure you want to delete this automatic transaction?"))
            return;

        try {
            await deleteAutoTransaction(txId);
            await loadAutoTransactions();
            if (onAutoTransactionsChanged) onAutoTransactionsChanged(); // ✅ trigger refresh
        } catch (err) {
            console.error("Error deleting:", err);
            alert("Failed to delete automatic transaction");
        }
    }, [loadAutoTransactions, onAutoTransactionsChanged]);


    const handleTriggerNow = async () => {
        if (!window.confirm("This will process all automatic transactions scheduled for today. Continue?"))
            return;

        try {
            await triggerAutoTransactionsNow();
            alert("Automatic transactions processed successfully! Check your balance and transaction history.");
        } catch (err) {
            console.error("Error triggering:", err);
            alert(err.message || "Failed to trigger automatic transactions");
        }
    };

    return (
        <motion.div
            className="w-full mx-auto mt-6 mb-10 px-3 sm:px-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.4, ease: "easeOut" }}
        >
            {/* Header */}
            <div className="flex flex-col items-center justify-center mb-5 text-center">
                <h2 className="text-xl sm:text-2xl font-bold text-white font-[Poppins] mb-3">
                    Automatic Transactions
                </h2>
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowAddForm(!showAddForm)}
                        className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-110 active:scale-95"
                        title="Add Automatic Transaction"
                    >
                        <Plus size={22} className="text-white" />
                    </button>
                    <button
                        onClick={handleTriggerNow}
                        className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-110 active:scale-95"
                        title="Process Today's Transactions"
                    >
                        <Play size={20} className="text-white" />
                    </button>
                </div>
            </div>

            {/* Add Form */}
            <AnimatePresence>
                {showAddForm && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="bg-gray-800 rounded-xl shadow-2xl p-6 mb-6 overflow-hidden"
                    >
                        <h3 className="text-xl font-semibold text-white mb-4">
                            Add Automatic Transaction
                        </h3>
                        <form onSubmit={handleAdd} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-300 mb-2 text-sm">Type</label>
                                    <select
                                        value={newTransaction.transaction_type}
                                        onChange={(e) =>
                                            setNewTransaction({
                                                ...newTransaction,
                                                transaction_type: e.target.value,
                                                transaction_category: "",
                                            })
                                        }
                                        className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-400"
                                        required
                                    >
                                        <option value="expense">Expense</option>
                                        <option value="income">Income</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-gray-300 mb-2 text-sm">Category</label>
                                    <select
                                        value={newTransaction.transaction_category}
                                        onChange={(e) =>
                                            setNewTransaction({
                                                ...newTransaction,
                                                transaction_category: e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-400"
                                        required
                                    >
                                        <option value="">Select Category</option>
                                        {getCurrentCategories(newTransaction.transaction_type).map((cat) => (
                                            <option
                                                key={
                                                    newTransaction.transaction_type === "income"
                                                        ? cat.id_income_category
                                                        : cat.id_expense_category
                                                }
                                                value={
                                                    newTransaction.transaction_type === "income"
                                                        ? cat.income_category_name
                                                        : cat.expense_category_name
                                                }
                                            >
                                                {newTransaction.transaction_type === "income"
                                                    ? cat.income_category_name
                                                    : cat.expense_category_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-gray-300 mb-2 text-sm">Amount</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={newTransaction.transaction_amount}
                                        onChange={(e) =>
                                            setNewTransaction({
                                                ...newTransaction,
                                                transaction_amount: e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-400"
                                        placeholder="0.00"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-300 mb-2 text-sm">Day of Month</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={newTransaction.day_of_month}
                                        onChange={(e) =>
                                            setNewTransaction({
                                                ...newTransaction,
                                                day_of_month: e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-400"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-gray-300 mb-2 text-sm">Description (Optional)</label>
                                <input
                                    type="text"
                                    value={newTransaction.description}
                                    onChange={(e) =>
                                        setNewTransaction({
                                            ...newTransaction,
                                            description: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-400"
                                    placeholder="e.g., Monthly rent"
                                />
                            </div>

                            <div className="flex gap-2 justify-end">
                                <button
                                    type="button"
                                    onClick={() => setShowAddForm(false)}
                                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:opacity-90 text-white rounded-lg transition"
                                >
                                    Add Transaction
                                </button>
                            </div>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Transaction List */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-gray-800 rounded-xl shadow-2xl overflow-hidden"
            >
                {loading ? (
                    <div className="p-8 text-center text-gray-400">
                        <div className="animate-pulse">Loading automatic transactions...</div>
                    </div>
                ) : error ? (
                    <div className="p-8 text-center text-red-400">{error}</div>
                ) : autoTransactions.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                        <Calendar size={48} className="mx-auto mb-4 opacity-30" />
                        <p className="text-lg mb-2">No automatic transactions set</p>
                        <p className="text-sm text-gray-500">
                            Click the + button to add recurring transactions
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Mobile Card View (<640px) - Now Scrollable */}
                        <div className="block sm:hidden relative">
                            <div className="block sm:hidden p-3 space-y-3 max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                                <AnimatePresence>
                                    {autoTransactions.map((tx, index) => (
                                        <AutoTransactionCard
                                            key={tx.id_auto_transaction}
                                            tx={tx}
                                            index={index}
                                            incomeCategories={incomeCategories}
                                            expenseCategories={expenseCategories}
                                            onSave={handleSave}
                                            onDelete={handleDelete}
                                        />
                                    ))}
                                </AnimatePresence>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-gray-800 to-transparent pointer-events-none"></div>
                        </div>
                        {/* Desktop Table View (≥640px) */}
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-900">
                                    <tr className="text-gray-300 text-left">
                                        <th className="px-6 py-4 font-semibold">Type</th>
                                        <th className="px-6 py-4 font-semibold">Category</th>
                                        <th className="px-6 py-4 font-semibold">Amount</th>
                                        <th className="px-6 py-4 font-semibold">Day</th>
                                        <th className="px-6 py-4 font-semibold">Description</th>
                                        <th className="px-6 py-4 font-semibold text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <AnimatePresence>
                                        {autoTransactions.map((tx, index) => (
                                            <AutoTransactionTableRow
                                                key={tx.id_auto_transaction}
                                                tx={tx}
                                                index={index}
                                                incomeCategories={incomeCategories}
                                                expenseCategories={expenseCategories}
                                                onSave={handleSave}
                                                onDelete={handleDelete}
                                            />
                                        ))}
                                    </AnimatePresence>
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </motion.div>
        </motion.div >
    );
}